import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient, type CreateOrder, type CreateOrderWritable } from '@api';
import { QueryKeys } from '@config';

/**
 * Creates an order and refreshes related order/artwork queries.
 *
 * Required params: CreateOrderWritable with venue identifier and positions.
 * Preconditions: user must be authenticated (bearer token via interceptor).
 * Important behavior: invalidates ORDERS and ARTWORKS query caches after success.
 *
 * @example
 * const { mutateAsync } = useCreateOrder();
 * await mutateAsync({ venue: venueSlug, positions: [{ artwork: artworkId, quantity: 1 }] });
 */
export const useCreateOrder = (options: object = {}) => {
  const queryClient = useQueryClient();

  return useMutation<CreateOrder, Error, CreateOrderWritable>({
    mutationFn: async (values) => {
      const response = await apiClient.ordersCreate<true>({
        body: values,
        throwOnError: true,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.ORDERS],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.ARTWORKS],
      });
    },
    ...options,
  });
};
